/* eslint-disable @typescript-eslint/no-unused-vars */

import { EventDispatcher } from "three";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import { Point } from "mapbox-gl";
class BaseController extends EventDispatcher {
  public static name = "BaseController";
  public static event = {
    COMPLETE: "complete",
  };

  protected _enabled: boolean = false;
  protected _mapboxViewer: MapboxViewer;
  protected _startPosition: { x: number; y: number } | null = null;
  protected _isDragging: boolean = false;
  protected _onDownPosition: Point = new Point(0, 0);
  protected _onUpPosition: Point = new Point(0, 0);
  protected _onMovePosition: Point = new Point(0, 0);
  protected _canvas: HTMLCanvasElement;

  constructor(mapboxViewer: MapboxViewer) {
    super();
    this._mapboxViewer = mapboxViewer;
    this._canvas = this._mapboxViewer._mapViewer.getCanvas();
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }
  get enabled(): boolean {
    return this._enabled;
  }

  @autobind
  protected onPointerDownController(event: PointerEvent) {
    if (!this._enabled) {
      return;
    }
    const canvasRect = this._canvas.getBoundingClientRect();
    this._onDownPosition.x = event.clientX - canvasRect.left;
    this._onDownPosition.y = event.clientY - canvasRect.top;
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    canvas.addEventListener("pointerup", this.onPointerUpController);
  }

  @autobind
  protected onPointerUpController(event: PointerEvent): boolean | void {
    if (!this._enabled) {
      return false;
    }
    const canvasRect = this._canvas.getBoundingClientRect();
    this._onUpPosition.x = event.clientX - canvasRect.left;
    this._onUpPosition.y = event.clientY - canvasRect.top;
    this.handleClick(event);
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    canvas.removeEventListener("pointerup", this.onPointerUpController);
  }

  @autobind
  protected handleClick(_event: PointerEvent) {
    console.log("handleClick");
  }

  @autobind
  protected onPointerMoveController(event: PointerEvent): boolean | void {
    if (!this._enabled) {
      return;
    }
    this._onMovePosition.x = event.clientX;
    this._onMovePosition.y = event.clientY;
  }

  /**
   * Computes the perpendicular distance from a pointer point (with lng, lat)
   * to a line segment defined by two coordinates.
   */
  @autobind
  private distanceFromPointToSegment(
    pointer: { lng: number; lat: number },
    coordA: number[],
    coordB: number[]
  ): number {
    const A = pointer.lng - coordA[0];
    const B = pointer.lat - coordA[1];
    const C = coordB[0] - coordA[0];
    const D = coordB[1] - coordA[1];

    // Dot product and squared length of the segment.
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx: number, yy: number;
    if (param < 0) {
      xx = coordA[0];
      yy = coordA[1];
    } else if (param > 1) {
      xx = coordB[0];
      yy = coordB[1];
    } else {
      xx = coordA[0] + param * C;
      yy = coordA[1] + param * D;
    }
    const dx = pointer.lng - xx;
    const dy = pointer.lat - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Given a line (an array of coordinates), the pointer position, and a candidate
   * "closest index" (for one of the coordinates), decide where to insert a new point.
   *
   * For example:
   * - If the candidate is index 1 and the pointer is closer to the segment from
   *   index 1 to 2 then the insertion index will be 1 + 1 (i.e. insert after index 1).
   * - If the candidate is index 2 and the pointer is closer to the segment from
   *   index 1 to 2 then the insertion index will be 2 (i.e. insert before index 2).
   *
   * Endpoints are handled explicitly.
   */
  @autobind
  protected getInsertionIndex(
    coordinates: number[][],
    pointer: { lng: number; lat: number },
    closestIndex: number
  ): number {
    // If candidate is the first point, then only option is to insert after it.
    if (closestIndex === 0) {
      return 1;
    }
    // If candidate is the last point, insert before it.
    if (closestIndex === coordinates.length - 1) {
      return closestIndex;
    }

    // Compute distances to the two segments sharing the candidate vertex.
    const distToPrev = this.distanceFromPointToSegment(
      pointer,
      coordinates[closestIndex - 1],
      coordinates[closestIndex]
    );
    const distToNext = this.distanceFromPointToSegment(
      pointer,
      coordinates[closestIndex],
      coordinates[closestIndex + 1]
    );

    // If the pointer is closer to the segment coming into the candidate vertex (i.e. the segment
    // from coordinates[closestIndex - 1] to coordinates[closestIndex]), insert before the candidate.
    // Otherwise, insert after.
    return distToPrev < distToNext ? closestIndex : closestIndex + 1;
  }

  @autobind
  detached() {
    console.log("detached");
  }

  @autobind
  complete() {
    console.log("complete");
  }

  @autobind
  dispose() {}
}

export default BaseController;
